import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';
import { ID_MAPPING } from '../infrastructure/InfrastructureConstants';
import { buildPageId } from '../AdminLinkUtils';
import {
    BREADCRUMB_TEXT_SUFFIX,
    DETAILS_TITLE,
    NAME_ASC_SORT_FIELD_INFO,
    PARENT_TITLE,
    SERVICE_TESTS_DATA_SOURCE,
    SERVICE_TESTS_LIST_ACTION_URLS,
    SERVICE_TESTS_LIST_FUNCTION_NAME,
    TYPE_VOIP,
} from './ServiceTestsConstants';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { getColumns } from './ServiceTestsColumns';
import { useFilterState } from '../../../stores/FilterStateStore';
import { useActionContext } from '../infrastructure/hooks/UseActionContext';
import { useServiceTestsDialogs } from './UseServiceTestsDialogs';
import { ServiceTestsListActions } from './ServiceTestsListActions';
import InfrastructureListBase from '../infrastructure/InfrastructureListBase';

const goToUrl = '../../';
const functionBreadCrumbs = [{ link: goToUrl, text: PARENT_TITLE }];

const getTestTypeTitle = (testTypeArrayObject, group) => {
    if (isEmpty(group)) {
        return '';
    }

    if (TYPE_VOIP === group) {
        return group;
    }

    if (isEmpty(testTypeArrayObject)) {
        return group;
    }

    const testType = testTypeArrayObject[0];
    return testType?.details?.title || group;
};

export default function ServiceTestsListPanel({ group, testTypeArrayObject }) {
    const typeIdsKey = testTypeArrayObject.map((type) => type._id).join(',');
    const typeIds = useMemo(() => typeIdsKey.split(',').filter(Boolean), [typeIdsKey]);
    const typeTitle = getTestTypeTitle(testTypeArrayObject, group);
    const breadcrumbs = useBreadcrumb(
        functionBreadCrumbs,
        null,
        null,
        DETAILS_TITLE,
        `${typeTitle} ${BREADCRUMB_TEXT_SUFFIX}`,
        {}
    );

    const columns = useMemo(() => getColumns(testTypeArrayObject, group), [testTypeArrayObject, group]);

    const [filterState] = useFilterState();
    const { updateContext: updateActionContext } = useActionContext();

    const { dialogElements, showScheduleDialogOpen, refresherRef } = useServiceTestsDialogs();

    // Render action buttons - called by InfrastructureListBase
    const renderActionButtons = useCallback(
        ({
            isDataTableActionButton,
            dataTableActioning,
            tableSelection,
            selectionData,
            tableState,
            handleConfirmedDataTableAction,
            refresher,
        }) => {
            // Update action context for executeAction handlers
            updateActionContext({
                handleConfirmedAction: handleConfirmedDataTableAction,
                tableSelection,
                tableState,
                filterState,
            });
            refresherRef.current = refresher;

            const handleScheduleClick = (updateHandlerCallback) => {
                let scheduling = {};
                const { selectionLength, currentTableSelection, totalRows } = selectionData.current;
                // Try with first select record scheduling
                if (selectionLength > 0) {
                    const rowsArray = totalRows ? Object.values(totalRows) : [];
                    let matchRow;
                    if (currentTableSelection.inverted) {
                        [matchRow] = rowsArray;
                    } else {
                        const selectedId = currentTableSelection.selection[0];
                        matchRow = rowsArray.find((rowObj) => rowObj?.row?.[ID_MAPPING] === selectedId);
                    }

                    if (matchRow) {
                        scheduling = matchRow.row.scheduling;
                    }
                }

                showScheduleDialogOpen(scheduling, testTypeArrayObject[0]?.scheduling, updateHandlerCallback);
            };

            return (
                <ServiceTestsListActions
                    isDataTableActionButton={isDataTableActionButton}
                    dataTableActioning={dataTableActioning}
                    tableSelection={tableSelection}
                    tableState={tableState}
                    handleConfirmedDataTableAction={handleConfirmedDataTableAction}
                    filterState={filterState}
                    selectionData={selectionData}
                    testTypes={typeIds}
                    testTypeArrayObject={testTypeArrayObject}
                    onScheduleClick={handleScheduleClick}
                />
            );
        },
        [updateActionContext, filterState, refresherRef, typeIds, showScheduleDialogOpen, testTypeArrayObject]
    );

    const dataSource = useMemo(() => {
        return {
            ...SERVICE_TESTS_DATA_SOURCE,
            params: {
                t: Date.now(),
                entityFields: SERVICE_TESTS_DATA_SOURCE.params.entityFields,
                query: JSON.stringify({ type: typeIds }),
            },
        };
    }, [typeIds]);

    return (
        <InfrastructureListBase
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(SERVICE_TESTS_LIST_FUNCTION_NAME)}
            tableId={`admin-service-tests-${group}-list`}
            dataSource={dataSource}
            columns={columns}
            sorters={NAME_ASC_SORT_FIELD_INFO}
            emptyText={t`No Test records found for this Service Test: ${group}`}
            idMapping={ID_MAPPING}
            actionUrls={SERVICE_TESTS_LIST_ACTION_URLS}
            filterElement={dialogElements}
            actionButtonsFn={renderActionButtons}
        />
    );
}

ServiceTestsListPanel.propTypes = {
    group: PropTypes.string,
    testTypeArrayObject: PropTypes.arrayOf(PropTypes.object),
};

ServiceTestsListPanel.defaultProps = {
    group: undefined,
    testTypeArrayObject: [],
};
